import { Button } from '@/components/ui/button';
import { Heart, Share2, Download, Trash } from 'lucide-react';
import { cn } from '@/lib/utils';

// Constants outside component - created once, reused always
const VARIANTS = {
  default: {
    container: 'flex items-center gap-2 w-40 flex-shrink-0 justify-end ml-auto',
    button: 'bg-zinc-700/30 hover:bg-zinc-700/50 border border-neutral-700',
    icon: 'text-white',
  },
  overlay: {
    container:
      'flex gap-2 opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-y-2 group-hover:translate-y-0',
    button: 'bg-background/80 backdrop-blur-sm hover:bg-background border-0',
    icon: 'text-muted-foreground',
  },
  minimal: {
    container: 'flex items-center gap-1',
    button: 'hover:bg-muted/50 border-0 bg-transparent',
    icon: 'text-muted-foreground hover:text-foreground',
  },
} as const;

const SIZES = {
  sm: { button: 'h-7 w-7', icon: 'h-3 w-3' },
  md: { button: 'h-8 w-8', icon: 'h-4 w-4' },
  lg: { button: 'h-10 w-10', icon: 'h-5 w-5' },
} as const;

interface ImageActionsProps {
  className?: string;
  onLike?: () => void;
  onShare?: () => void;
  onDownload?: () => void;
  onDelete?: () => void;
  isLiked?: boolean;
  variant?: keyof typeof VARIANTS;
  size?: keyof typeof SIZES;
}

export default function ImageActions({
  className,
  onLike,
  onShare,
  onDownload,
  onDelete,
  isLiked = false,
  variant = 'default',
  size = 'md',
}: ImageActionsProps) {
  const currentVariant = VARIANTS[variant];
  const currentSize = SIZES[size];

  return (
    <div className={cn(currentVariant.container, className)}>
      <Button
        className={cn(currentVariant.button, currentSize.button, 'p-0')}
        size='icon'
        onMouseDown={onLike}
      >
        <Heart
          className={cn(
            currentSize.icon,
            'transition-colors',
            isLiked ? 'fill-red-500 text-red-500' : currentVariant.icon,
          )}
        />
      </Button>

      <Button
        className={cn(currentVariant.button, currentSize.button, 'p-0')}
        size='icon'
        onMouseDown={onShare}
      >
        <Share2 className={cn(currentSize.icon, currentVariant.icon)} />
      </Button>

      <Button
        className={cn(currentVariant.button, currentSize.button, 'p-0')}
        size='icon'
        onMouseDown={onDownload}
      >
        <Download className={cn(currentSize.icon, currentVariant.icon)} />
      </Button>

      <Button
        className={cn(currentVariant.button, currentSize.button, 'p-0')}
        size='icon'
        onMouseDown={onDelete}
      >
        <Trash className={cn(currentSize.icon, currentVariant.icon)} />
      </Button>
    </div>
  );
}
